import { find, sortBy, last } from 'lodash-es'
import { extractTermFromGQLformatting } from 'ppbl/utils/common-utils'
import { Offer } from '../types'

/**
 * Calculates financial amounts
 */

export class FinancialCalculatorService {
  /**
   * Weeks per year constant
   */
  public readonly weeksPerYear = 52

  /**
   * Months per year constant
   */
  public readonly monthsPerYear = 12

  /**
   * Business days per month constant
   */
  public readonly businessDaysPerMonth = 20

  /**
   * Cannot find offer for term message.
   */
  public readonly cannotFindOfferForTermMessage = 'Cannot find offer for term'

  /**
   * Monthly revenue must be greater than zero error message.
   */
  public readonly monthlyRevenueMustBeGreaterThanZeroMessage =
    'Monthly revenue must be greater than zero.'

  /**
   * Offer amount must be greater than zero error message.
   */
  public readonly offerAmountMustBeGreaterThanZeroMessage =
    'Offer amount must be greater than zero.'

  /**
   * Term in months must be greater than zero error message.
   */
  public readonly termInMonthsMustBeGreaterThanZeroMessage =
    'Term in months must be greater than zero.'

  /**
   * Term in weeks must be greater than zero error message.
   */
  public readonly termInWeeksMustBeGreaterThanZeroMessage =
    'Term in weeks must be greater than zero.'

  /**
   * Weekly average gross must be greater than zero error message.
   */
  public readonly weeklyAverageGrossMustBeGreaterThanZeroMessage =
    'Weekly average gross must be greater than zero.'

  /**
   * Weekly revenue must be greater than zero error message.
   */
  public readonly weeklyRateMustBeGreaterThanZeroMessage =
    'Weekly rate must be greater than zero.'

  /**
   * Weekly revenue must be greater than zero error message.
   */
  public readonly weeklyRevenueMustBeGreaterThanZeroMessage =
    'Weekly revenue must be greater than zero.'

  /**
   * Gets the annual rate
   * @param offers Offers array
   * @param weeklyRate
   * @param termInWeeks
   */
  getMatchingOffer(offers: Offer[], weeklyRate: number, termInWeeks: number) {
    let offer = find(
      offers,
      (currOffer) =>
        currOffer.weeklyInterestRate === weeklyRate &&
        extractTermFromGQLformatting(currOffer.term) === termInWeeks,
    )

    if (!offer) {
      const sortedOffers = sortBy(offers, [
        (currOffer) => extractTermFromGQLformatting(currOffer.term),
        (currOffer) => currOffer.weeklyInterestRate,
      ])
      offer = last(sortedOffers)
    }

    return offer
  }

  /**
   * Gets annual percentage rate values
   * @param offers The offers to calculate against
   * @param weeklyRate The weekly rate value to use
   * @param termInWeeks The length of the term in weeks to use
   */
  getAnnualPercentageRate(
    offers: Offer[],
    weeklyRate: number,
    termInWeeks: number,
  ) {
    const offer = this.getMatchingOffer(offers, weeklyRate, termInWeeks)
    return offer ? offer.annualPercentageRate : undefined
  }

  /**
   * Gets annual interest rate values
   * @param offers The offers to calculate against
   * @param weeklyRate The weekly rate value to use
   * @param termInWeeks The length of the term in weeks to use
   */
  getAnnualInterestRate(
    offers: Offer[],
    weeklyRate: number,
    termInWeeks: number,
  ) {
    const offer = this.getMatchingOffer(offers, weeklyRate, termInWeeks)
    return offer ? offer.annualInterestRate : undefined
  }

  /**
   * Gets cents on the dollar amount
   * @param offerAmount
   * @param termInWeeks
   * @param weeklyRate
   */
  getCentsOnTheDollar(offerAmount, termInWeeks, weeklyRate) {
    if (!offerAmount) {
      throw new Error(this.offerAmountMustBeGreaterThanZeroMessage)
    }

    return (offerAmount * termInWeeks * weeklyRate) / offerAmount
  }

  /**
   * Gets daily collection amount
   * @param purchaseAmount
   * @param termInMonths
   * @param businessDaysPerMonth
   */
  getDailyCollectionAmount(purchaseAmount, termInMonths) {
    if (!termInMonths) {
      throw new Error(this.termInMonthsMustBeGreaterThanZeroMessage)
    }

    return purchaseAmount / (termInMonths * this.businessDaysPerMonth)
  }

  /**
   * Gets daily collection proportion of revenue
   * @param dailyCollectionAmount
   * @param monthlyRevenue
   */
  getDailyCollectionProportionOfRevenue(dailyCollectionAmount, monthlyRevenue) {
    if (!monthlyRevenue) {
      throw new Error(this.monthlyRevenueMustBeGreaterThanZeroMessage)
    }

    return dailyCollectionAmount / (monthlyRevenue / this.businessDaysPerMonth)
  }

  /**
   * Gets FRSA split percentage
   * @param repaymentAmount
   * @param weeklyRevenue
   * @param termInWeeks
   */
  getFRSASplitPercentage(repaymentAmount, weeklyRevenue, termInWeeks) {
    if (!termInWeeks) {
      throw new Error(this.termInWeeksMustBeGreaterThanZeroMessage)
    }

    if (!weeklyRevenue) {
      throw new Error(this.weeklyRevenueMustBeGreaterThanZeroMessage)
    }

    return repaymentAmount / (weeklyRevenue * termInWeeks)
  }

  /**
   * Gets offer by term
   * @param offers Offers array
   * @param termInWeeks
   */
  getOfferByTerm(offers: Offer[], termInWeeks) {
    if (!offers) {
      throw new Error(`${this.cannotFindOfferForTermMessage}' '${termInWeeks}`)
    }

    return find(
      offers,
      (offer) => extractTermFromGQLformatting(offer.term) === termInWeeks,
    )
  }

  /**
   * Get max proportion of revenue
   * (formerly defined as getPercentOfGross)
   * @param offerAmount
   * @param termInWeeks
   * @param weeklyAverageGross
   */
  getMaxProportionOfRevenue(offerAmount, termInWeeks, weeklyAverageGross) {
    if (!termInWeeks) {
      throw new Error(this.termInWeeksMustBeGreaterThanZeroMessage)
    }

    if (!weeklyAverageGross) {
      throw new Error(this.weeklyAverageGrossMustBeGreaterThanZeroMessage)
    }

    return offerAmount / (weeklyAverageGross * termInWeeks)
  }

  /**
   * Gets the max proportion of revenue for term
   * (formerly defined as getPercentOfGrossForTerm)
   * @param termInWeeks
   * @param offers
   */
  getMaxProportionOfRevenueForTerm(termInWeeks, offers: Offer[]) {
    if (!termInWeeks || !offers) {
      return 0
    }

    const offer = find(
      offers,
      (currOffer) =>
        extractTermFromGQLformatting(currOffer.term) === termInWeeks,
    )

    return offer ? offer.maxProportionOfRevenue : 0
  }

  /**
   * Gets term in months
   * @param termInWeeks
   */
  getTermInMonths(termInWeeks) {
    const termInMonths = Math.round(termInWeeks / 4.334)

    return termInMonths >= 1 ? termInMonths : 1
  }

  /**
   * Gets total finance charges
   * @param offerAmount
   * @param termInWeeks
   * @param weeklyRate
   */
  getTotalFinanceCharges(offerAmount, termInWeeks, weeklyRate) {
    return offerAmount * termInWeeks * weeklyRate
  }

  /**
   * Gets purchase amount
   * @param offerAmount
   * @param totalInterestCharge
   */
  getPurchaseAmount(offerAmount, totalInterestCharge) {
    return offerAmount + totalInterestCharge
  }

  /**
   * Gets total amount of payments
   * @param offerAmount
   * @param termInWeeks
   * @param weeklyRate
   */
  getTotalAmountOfPayments(offerAmount, termInWeeks, weeklyRate) {
    return offerAmount * termInWeeks * weeklyRate + offerAmount
  }

  /**
   * Gets total interest charge amount
   * @param termInWeeks
   * @param weeklyInterestAmount
   */
  getTotalInterestCharge(termInWeeks, weeklyInterestAmount) {
    return termInWeeks * weeklyInterestAmount
  }

  /**
   * Gets total interest percent
   * @param termInWeeks
   * @param weeklyPrice
   */
  getTotalInterestPercent(termInWeeks, weeklyPrice) {
    return termInWeeks * weeklyPrice
  }

  /**
   * Gets weekly interest payment
   * @param offerAmount
   * @param weeklyRate
   */
  getWeeklyInterestPayment(offerAmount, weeklyRate) {
    return offerAmount * weeklyRate
  }

  /**
   * Gets weekly collection amount
   * @param purchaseAmount
   * @param termInWeeks
   */
  getWeeklyCollectionAmount(purchaseAmount, termInWeeks) {
    if (!termInWeeks) {
      throw new Error(this.termInWeeksMustBeGreaterThanZeroMessage)
    }

    return purchaseAmount / termInWeeks
  }

  /**
   * Gets weekly collection proportion of revenue
   * @param weeklyCollectionAmount
   * @param monthlyRevenue
   */
  getWeeklyCollectionProportionOfRevenue(
    weeklyCollectionAmount,
    monthlyRevenue,
  ) {
    if (!monthlyRevenue) {
      throw new Error(this.monthlyRevenueMustBeGreaterThanZeroMessage)
    }

    return (
      weeklyCollectionAmount /
      (monthlyRevenue / (this.weeksPerYear / this.monthsPerYear))
    )
  }

  /**
   * Gets weekly price by offer
   * @param termInWeeks
   * @param offers
   */
  getWeeklyPriceByOffer(termInWeeks, offers: Offer[]) {
    if (!termInWeeks || !offers) {
      return 0
    }

    // const filteredOffer = offers.filter(currOffer => currOffer.term === termInWeeks)
    const offer = find(
      offers,
      (currOffer) =>
        extractTermFromGQLformatting(currOffer.term) === termInWeeks,
    )

    return offer ? parseFloat(offer.weeklyInterestRate.toFixed(6)) : 0
  }

  /**
   * Gets weekly principal payment amount
   * @param offerAmount
   * @param termInWeeks
   */
  getWeeklyPrincipalPayment(offerAmount, termInWeeks) {
    if (!termInWeeks) {
      throw new Error(this.termInWeeksMustBeGreaterThanZeroMessage)
    }

    return offerAmount / termInWeeks
  }

  /**
   * Gets weekly revenue
   * @param monthlyRevenue
   */
  getWeeklyRevenue(monthlyRevenue) {
    if (!monthlyRevenue) {
      return 1
    }

    return Math.ceil((monthlyRevenue * 12 * 100) / 52) / 100
  }

  /**
   * Gets weekly total payment amount
   * @param offerAmount
   * @param termInWeeks
   * @param weeklyRate
   */
  getWeeklyTotalPayment(offerAmount, termInWeeks, weeklyRate) {
    if (!termInWeeks) {
      throw new Error(this.termInWeeksMustBeGreaterThanZeroMessage)
    }

    return offerAmount / termInWeeks + offerAmount * weeklyRate
  }

  /**
   * Determines if the amount/term is qualified
   * @param offerAmount
   * @param termInWeeks
   * @param maxProportionOfRevenue Maximum offer amount percentage allowable as portion of revenue
   * (Note: maxProportionOfRevenue was formerly offer amount allowable as percent of gross (revenue))
   * @param maximumLoanAmount Maximum loan amount
   * @param offers Offers array
   */
  isQualified(
    offerAmount,
    termInWeeks,
    maxProportionOfRevenue,
    maximumLoanAmount,
    offers: Offer[],
  ) {
    const maxProportionOfRevenueForTerm = this.getMaxProportionOfRevenueForTerm(
      termInWeeks,
      offers,
    )

    return (
      offerAmount <= maximumLoanAmount &&
      maxProportionOfRevenue <= maxProportionOfRevenueForTerm
    )
  }

  /**
   * Gets weekly interest rate
   * @param totalInterestPercentage
   * @param termInWeeks
   */
  getWeeklyInterestRate(totalInterestPercentage, termInWeeks) {
    if (!termInWeeks) {
      throw new Error(this.termInWeeksMustBeGreaterThanZeroMessage)
    }

    const weeklyInterestRate = totalInterestPercentage / 100 / termInWeeks
    return isNaN(weeklyInterestRate) ? 0 : weeklyInterestRate
  }
}

export const financialCalculatorService = new FinancialCalculatorService()
